import { NgClass } from '@angular/common';
import { AfterViewInit, Component, effect, ElementRef, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatRipple } from '@angular/material/core';
import { MatDivider } from '@angular/material/divider';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatList, MatListItem } from '@angular/material/list';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatSidenav, MatSidenavContainer, MatSidenavContent } from '@angular/material/sidenav';
import { MatToolbar, MatToolbarRow } from '@angular/material/toolbar';
import { RouterLink, RouterOutlet } from '@angular/router';
import { SwUpdate, VersionEvent } from '@angular/service-worker';

import { TranslateService } from '@ngx-translate/core';
import { stringify } from 'qs';
import { delay, filter, from, take } from 'rxjs';

import { environment } from 'src/environments/environment';

import * as APP_VERSION from '../../package.json';
import { DebugService } from './services/debug.service';
import { DialogService } from './services/dialog.service';
import { UiService } from './services/ui.service';
import { UserService } from './services/user.service';


@Component({
	selector: 'ripeurs-operator-app-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	encapsulation: ViewEncapsulation.None,
	standalone: true,
	imports: [
		NgClass,
		MatDivider,
		MatIcon,
		MatIconButton,
		MatList,
		MatListItem,
		MatProgressBar,
		MatRipple,
		MatSidenav,
		MatSidenavContainer,
		MatSidenavContent,
		MatToolbar,
		MatToolbarRow,
		RouterLink,
		RouterOutlet,
	],
})
export class AppComponent implements OnInit, AfterViewInit {
	private static readonly PWA_RECOVERY_KEY = 'ripeurs-operator-app-pwa-recovery';
	private static readonly PWA_RECOVERY_COOLDOWN = 60_000;

	@ViewChild('router', { static: false }) public routerElement!: ElementRef;
	public readonly header = this._ui.header.value;
	public readonly user = this._user.user;
	public readonly region = this._user.region;
	public readonly logged = this._user.logged;
	public readonly APP_VERSION: string;

	constructor(
		public readonly _ui: UiService,
		private readonly _debug: DebugService,
		private readonly _dialog: DialogService,
		private readonly _swUpdate: SwUpdate,
		private readonly _translate: TranslateService,
		private readonly _user: UserService,
	) {
		this.APP_VERSION = Object.create(APP_VERSION)?.version;
		this._translate.use('fr-FR');

		effect(() => {
			this._ui.header.set({ toolbarShow: this._user.logged() });
		});
		effect(() => {
			const region = this._user.region();
			if (region?.id) this._ui.setTheme(region.id as any);
		});

		if (!environment.production) {
			const body = document.querySelector('body');
			if (body !== null) body.classList.add('environment-development');
		}
	}

	public ngOnInit(): void {
		this.upgradePWACheck();
	}

	public ngAfterViewInit(): void {
		this._ui.content.subject.pipe(
			delay(200),
			filter(({ scroll }) => scroll !== undefined),
			filter(() => this.routerElement.nativeElement.parentElement.querySelector('ng-component')),
		).subscribe(({ scroll }) => {
			this.routerElement
				.nativeElement
				.parentElement
				.querySelector('ng-component')
				.scroll(...scroll!);
		});
	}

	public openSimulator(): void { this._dialog.openSimulator(); }

	public openPartSimulator(): void { this._dialog.openSimulator(true); }

	public sidenavDebug(): void { this._debug.dialogDebug(); }

	public openEquipment(): void {
		const urlParams = stringify({
			prefill_Firstname: this.user()?.firstName ?? '',
			prefill_Name: this.user()?.lastName ?? '',
		});
		window.open(`https://airtable.com/shrpFus5NpbnALRfD?${urlParams}`, '_blank');
	}

	public openExpenseReportForm(): void {
		const urlParams = stringify({
			prefill_prenom: this.user()?.firstName ?? '',
			prefill_nom: this.user()?.lastName ?? '',
		});
		window.open(`https://airtable.com/shrvzMzUNj7TFqIHA?${urlParams}`, '_blank');
	}

	private upgradePWACheck(): void {
		if (this._swUpdate.isEnabled) {
			this._swUpdate.versionUpdates.pipe(
				filter(({ type }: VersionEvent) => type === 'VERSION_READY'),
			).subscribe({
				next: () => {
					const dialog = this._dialog.openMessage({
						title: 'Nouvelle version',
						content: `Une nouvelle version de l'app est disponible. Mettre à jour ?`,
						close: true,
						buttons: [
							{
								iconLeft: 'close',
								title: 'Non, plus tard',
								click: () => dialog.close(),
							},
							{
								iconLeft: 'restart_alt',
								title: 'Oui, redémarrer',
								click: () => this.applyUpdate(dialog),
							},
						],
					});
				},
			});

			this._swUpdate.unrecoverable.pipe(take(1)).subscribe(({ reason }) => {
				console.error('Unrecoverable service worker state:', reason);
				void this.recoverFromUnrecoverablePwaState();
			});
		}
	}

	private async recoverFromUnrecoverablePwaState(): Promise<void> {
		const lastRecovery = Number(sessionStorage.getItem(AppComponent.PWA_RECOVERY_KEY));
		if (Date.now() - lastRecovery < AppComponent.PWA_RECOVERY_COOLDOWN) return;

		sessionStorage.setItem(AppComponent.PWA_RECOVERY_KEY, String(Date.now()));

		try {
			const registration = await navigator.serviceWorker.getRegistration('/');
			await registration?.unregister();

			if ('caches' in globalThis) {
				const cacheNames = await globalThis.caches.keys();
				await Promise.all(
					cacheNames
						.filter((cacheName) => cacheName.startsWith('ngsw:'))
						.map((cacheName) => globalThis.caches.delete(cacheName)),
				);
			}
		} finally {
			window.location.reload();
		}
	}

	private applyUpdate(dialog: MatDialogRef<unknown, any>): void {
		dialog.close();
		this._dialog.openMessage({ title: 'Redémarrage...', contentLoader: true });

		from(this._swUpdate.activateUpdate()).pipe(
			take(1),
		).subscribe({
			next: () => window.location.reload(),
			error: () => window.location.reload(),
		});
	}
}
